import Pagination from "@/components/ui/table/pagination-table";
import { useMemo, useState } from "react";
import PushNotificationForm from "../modal/push-notification-form";
import { ConfirmationDialog } from "@/components/ui/common/confirmation-dailog"

interface SearchCriteriaType {
  limit: number;
  offset: number;
  sort_order: string;
  sort_key: string;
  exclude_keys: string;
  type: string;
}

const initialValues = {
  limit: 10,
  offset: 0,
  sort_order: "desc",
  sort_key: "id",
  exclude_keys: "membership_cancellation",
  type: "trigger",
}

const PushNotification = () => {
  const [isOpen, setOpen] = useState(false);
  const [action, setAction] = useState<"add" | "edit">("add");
  const [data, setData] = useState<EmailTrigger | undefined>(undefined);

  const {
    searchCriteria,
    query,
    toggleSortOrder,
    setSearchCriteria,
    inputValue,
    setInputValue,
  } = useTableData(initialValues);

  const { data: emailTriggerData, refetch } = useGetEmailTriggersQuery({
    query: query,
  })

  const emailTriggers = useMemo(() => {
    return Array.isArray(emailTriggerData?.data) ? emailTriggerData?.data : [];
  }, [emailTriggerData])

  const totalRecords = emailTriggerData?.filtered_counts || 0;

  const {
    handleLimitChange,
    handleNextPage,
    handlePrevPage,
    handleFirstPage,
    handleLastPage,
    isLastPage,
  } = usePagination<SearchCriteriaType>({
    totalRecords,
    searchCriteria,
    setSearchCriteria,
  });

  const handleEdit = (data: EmailTrigger) => {
    console.log("Edit notification:", data);
    setAction("edit");
    setData(data);
    setOpen(true);
  };



  return (
    <div className="p-4 mx-auto border-[1px] border-gray-200 rounded-md">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-medium">Email and Push Notifications</h2>

        </div>
      </div>

      <div className="space-y-3">
        {emailTriggers.map((notification) => (
          <div
            key={notification.id}
            className="border rounded-lg px-3 py-2 hover:border-green-500/50 transition-colors"
          >
            <div className="flex items-start justify-between">
              <div className="space-y-3 flex-1">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="space-y-2">
                    <div className="flex flex-wrap gap-1">
                      <span className="text-sm font-medium text-nowrap">Trigger:</span>
                      <span className="text-sm text-green-500 line-clamp-1">
                        {notification.trigger_name}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      <span className="text-sm font-medium text-nowrap">Email Subject:</span>
                      <span className="text-sm text-muted-foreground line-clamp-1">
                        {notification.email_subject}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      <span className="text-sm font-medium text-nowrap">Notification Title:</span>
                      <span className="text-sm text-muted-foreground line-clamp-1">
                        {notification.notification_title}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-row sm:flex-col justify-start sm:justify-end items-center sm:items-end gap-2 flex-shrink-0">
                    <button
                      type="button"
                      title="Edit"
                      onClick={() => handleEdit(notification)}
                      className="flex items-center gap-2 px-3 py-1 text-sm font-medium text-primary rounded-md transition-colors"
                    >
                      <i className="fa fa-pencil"></i>
                    </button>
                    <NotificationTypeToggle
                      notification={notification}
                      refetch={refetch}
                    />
                  </div>
                </div>
              </div>
            </div>


          </div>
        ))}
        {emailTriggers.length > 0 && (
          <Pagination
            limit={searchCriteria.limit}
            offset={searchCriteria.offset}
            totalItems={totalRecords}
            onLimitChange={handleLimitChange}
            onNextPage={handleNextPage}
            onPrevPage={handlePrevPage}
            onFirstPage={handleFirstPage}
            onLastPage={handleLastPage}
            isLastPage={isLastPage}
          />
        )}
      </div>
      <PushNotificationForm
        editModeData={data}
        isOpen={isOpen}
        setOpen={setOpen}
        action={action}
        setAction={setAction}
        refetch={refetch}
      />
    </div>
  );
};

export default PushNotification;


import { cn } from "@/lib/utils"
import { useGetEmailTriggersQuery } from "@/services/systemConfigApi";
import { EmailTrigger } from "@/types/system-setting/system-config";
import { useUpdateEmailTriggerMutation } from "@/services/systemConfigApi";
import useTableData from "@/components/ui/table/data-table-logic";
import usePagination from "@/hooks/use-pagination";
import { useToast } from "@/components/ui/use-toast";

interface NotificationTypeToggleProps {
  notification: EmailTrigger;
  refetch: () => void;
  className?: string;
}

export function NotificationTypeToggle({ notification, refetch, className }: NotificationTypeToggleProps) {
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [pendingToggle, setPendingToggle] = useState<{
    type: 'email' | 'notification';
    enabled: boolean;
  } | null>(null);

  const [updateEmailTrigger] = useUpdateEmailTriggerMutation();
  const { toast } = useToast();

  const isPasswordSetup = notification.key === "password_setup" && notification.is_email_enabled;

  const handleToggle = async (type: 'email' | 'notification', enabled: boolean) => {
    try {
      const updatedData = {
        ...notification,
        is_email_enabled: type === 'email' ? enabled : notification.is_email_enabled,
        is_notification_enabled: type === 'notification' ? enabled : notification.is_notification_enabled,
      };

      const resp = await updateEmailTrigger(updatedData).unwrap();
      if (resp) {
        toast({
          variant: "success",
          title: `Trigger ${type} ${enabled ? "enabled" : "disabled"} successfully.`,
        });
        refetch();
      }
    } catch (error) {
      console.error("Failed to update trigger:", error);
      toast({
        variant: "destructive",
        title: "Failed to update trigger",
      });
    }
  };

  const handleAskConfirmation = (type: 'email' | 'notification', enabled: boolean) => {
    setPendingToggle({ type, enabled });
    setDialogOpen(true);
  };

  const handleConfirm = () => {
    if (pendingToggle) {
      handleToggle(pendingToggle.type, pendingToggle.enabled);
      setDialogOpen(false);
      setPendingToggle(null);
    }
  };

  const handleCancel = () => {
    setDialogOpen(false);
    setPendingToggle(null);
  };

  return (
    <>
      <div className={cn("flex flex-wrap gap-2 sm:gap-4 items-center", className)}>
        <button
          type="button"
          disabled={isPasswordSetup}
          title={isPasswordSetup ? "This email trigger cannot be disabled" : ""}
          onClick={() => handleAskConfirmation('email', !notification.is_email_enabled)}
          className={cn(
            "px-4 py-1 rounded-md text-sm font-medium transition-colors",
            isPasswordSetup && "opacity-50 cursor-not-allowed bg-green-200 text-green-700",
            !isPasswordSetup && notification.is_email_enabled && "bg-green-100 text-green-700 hover:bg-green-200",
            !isPasswordSetup && !notification.is_email_enabled && "bg-gray-100 text-gray-500 hover:bg-gray-200"
          )}
        >
          <div className="flex items-center gap-2">Email</div>
        </button>

        <button
          type="button"
          onClick={() => handleAskConfirmation('notification', !notification.is_notification_enabled)}
          className={cn(
            "px-4 py-1 rounded-md text-sm font-medium transition-colors",
            notification.is_notification_enabled
              ? "bg-green-100 text-green-700 hover:bg-green-200"
              : "bg-gray-100 text-gray-500 hover:bg-gray-200"
          )}
        >
          <div className="flex items-center gap-2">Notification</div>
        </button>
      </div>

      <ConfirmationDialog
        isOpen={isDialogOpen}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        title="Are you sure?"
        message={`You're about to ${pendingToggle?.enabled ? "enable" : "disable"} the ${
          pendingToggle?.type === "email" ? "Email" : "Notification"
        } trigger.`}
      />
    </>
  );
}
